import { JBAction } from './actions'

// Singleton worker instance to avoid creating multiple workers
let validationWorker = null
let validationTimeout = null

const DEBOUNCE_DELAY = 300 // ms

/**
 * Get or create the validation worker
 * @returns {Worker|null}
 */
const getValidationWorker = () => {
  if (typeof window === 'undefined' || !window.Worker) {
    return null
  }

  if (!validationWorker) {
    try {
      validationWorker = new Worker(
        new URL('../../workers/journey-builder-worker.js', import.meta.url),
        { type: 'module' }
      )
    } catch {
      return null
    }
  }

  return validationWorker
}

/**
 * Terminate the validation worker
 */
export const terminateValidationWorker = () => {
  if (validationWorker) {
    validationWorker.terminate()
    validationWorker = null
  }
  if (validationTimeout) {
    clearTimeout(validationTimeout)
    validationTimeout = null
  }
}

/**
 * Set up worker message handler (only once)
 */
const setupWorkerHandler = (worker, store) => {
  if (worker._handlerSetup) {
    return
  }

  worker.onmessage = event => {
    const { type, result: validationResult, errorCount } = event.data

    if (type === 'VALIDATION' && validationResult) {
      // Get fresh state to avoid stale references
      const currentState = store.getState()
      const currentWorkflow = currentState.workflow

      if (!currentWorkflow?.nodes) {
        return
      }

      // Create updated nodes with validation results
      const updatedNodes = { ...currentWorkflow.nodes }
      Object.keys(validationResult).forEach(nodeId => {
        if (updatedNodes[nodeId]?.ui) {
          updatedNodes[nodeId] = {
            ...updatedNodes[nodeId],
            ui: {
              ...updatedNodes[nodeId].ui,
              validation: validationResult[nodeId]
            }
          }
        }
      })

      store.dispatch({
        type: JBAction.BATCH_WORKFLOW_UPDATE,
        payload: {
          nodes: updatedNodes,
          links: currentWorkflow.links
        }
      })

      store.dispatch({
        type: JBAction.UPDATE_WORKFLOW_ERROR_STACK,
        payload: {
          errors: validationResult,
          count: errorCount
        }
      })
    }
  }

  worker.onerror = _error => {
  }

  worker._handlerSetup = true
}

/**
 * Trigger background validation (non-blocking)
 */
const triggerBackgroundValidation = (store, immediate = false) => {
  // Clear any pending validation
  if (validationTimeout) {
    clearTimeout(validationTimeout)
  }

  const runValidation = () => {
    const state = store.getState()
    const workflow = state.workflow

    // Skip if no nodes
    if (!workflow?.nodes || Object.keys(workflow.nodes).length === 0) {
      if (state.ui.errorStack.count) {
        // reset error stack
        store.dispatch({
          type: JBAction.UPDATE_WORKFLOW_ERROR_STACK,
          payload: {
            errors: {},
            count: 0
          }
        })
      }

      return
    }

    const worker = getValidationWorker()
    if (!worker) {
      return
    }

    setupWorkerHandler(worker, store)

    // Send only type+subtype+json_schema — keeps message lean, no ui_schema bloat
    const paletteNodes = state.ui?.palette?.nodes
    const palette = Array.isArray(paletteNodes)
      ? paletteNodes.map(({ type, subtype, json_schema }) => ({ type, subtype, json_schema }))
      : []

    // Send validation request to worker
    worker.postMessage({
      type: 'VALIDATION',
      data: workflow,
      palette
    })
  }

  if (immediate) {
    runValidation()
  } else {
    validationTimeout = setTimeout(runValidation, DEBOUNCE_DELAY)
  }
}

export const workflowValidatorMiddleware = store => next => action => {
  const typesToValidate = [
    JBAction.LOAD_WORKFLOW,
    'SET_PALETTE_NODES',
    JBAction.LINK_CREATED,
    JBAction.LINK_REMOVED,
    JBAction.NODE_REPLACE,
    JBAction.NODE_REMOVED,
    JBAction.NODE_NEXT_CONNECTION_CREATED,
    JBAction.NODE_DROPPED_FROM_PALETTE,
    JBAction.NODE_FORMDATA_CHANGED,
    JBAction.NODE_DATA_CHANGED,
    JBAction.NODE_VALIDATE,
    JBAction.VALIDATE_WORKFLOW,
    JBAction.LINK_TARGET_REPLACED,
    JBAction.PASTE,
    JBAction.UNDO,
    JBAction.REDO,
  ]

  // Always pass the action through immediately - never block Redux
  const result = next(action)

  // Skip validation for actions dispatched by the validator itself to prevent loops
  const skipValidationActions = [
    JBAction.BATCH_WORKFLOW_UPDATE,
    JBAction.UPDATE_WORKFLOW_ERROR_STACK,
    JBAction.ZOOM_SET,
    JBAction.ZOOM_TO_FIT
  ]

  if (skipValidationActions.includes(action.type)) {
    return result
  }

  // Trigger background validation asynchronously if needed
  if (typesToValidate.includes(action.type)) {
    const isImmediate = action.type === JBAction.VALIDATE_WORKFLOW || action.type === JBAction.NODE_FORMDATA_CHANGED
    if (isImmediate) {
      triggerBackgroundValidation(store, true)
    } else if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(() => triggerBackgroundValidation(store), { timeout: 500 })
    } else {
      setTimeout(() => triggerBackgroundValidation(store), 0)
    }
  }

  return result
}
